'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { ToastContainer, toast, ToastOptions } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

type ToastMessageType = 'success' | 'error' | 'warning';

interface ToastContextProps {
  showToastMessage: (message: string, type?: ToastMessageType) => void;
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
  showWarningMessage: (message: string) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const showToastMessage = (message: string, type: ToastMessageType = 'success') => {
    const options: ToastOptions<object> | undefined = {
      position: 'top-center',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    };

    switch (type) {
      case 'success':
        toast.success(message, options);
        break;
      case 'error':
        toast.error(message, options);
        break;
      case 'warning':
        toast.warning(message, options);
        break;
      default:
        toast(message, options);
    }
  };

  const showSuccessMessage = (message: string) => showToastMessage(message, 'success');
  const showErrorMessage = (message: string) => showToastMessage(message, 'error');
  const showWarningMessage = (message: string) => showToastMessage(message, 'warning');

  return (
    <ToastContext.Provider value={{ showToastMessage, showSuccessMessage, showErrorMessage, showWarningMessage }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
